/**
 * MLinkOne PO (Doc850) Workbench API service. Admin-gated read surface (edi.po.*):
 * browse the canonical PO list + open the drill-down. Mirrors packAnalysisService's
 * thin-wrapper-over-apiClient shape. DTOs match the backend Po_List_Response /
 * Po_GetActiveVersion_Response payloads.
 */
import { apiClient } from '@/app/utils/apiClient';
import {
  API_MLINKONE_PO_LIST,
  API_MLINKONE_PO_CANONICAL,
  API_MLINKONE_PO_INGEST,
  API_MLINKONE_PO_REJECTED,
  API_MLINKONE_PO_REPROCESS,
} from '@/app/constants/apiConstants';

/** Standard SmartMatrix Result<T> envelope. */
export interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

// ── List ────────────────────────────────────────────────────────────────────

export interface PoListItem {
  poId: number;
  poNumber: string;
  versionNo: number;
  isCurrent: boolean;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  orderQty?: number | null;
  lineCount: number;
  contentHash?: string | null;
  sourceFormat?: string | null;
  createdAt?: string | null;
  /** DOC850 DOCPURPOSE raw code — drives the Cancelled (01) / Deleted (03) badge. */
  docPurpose?: string | null;
}

export interface PoListResponse {
  items: PoListItem[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  message: string;
}

export interface PoListParams {
  tenantId: number;
  /** Active buyer party (top-bar) — scopes the list to this buyer. */
  buyerPartyId?: number | null;
  /** Active supplier party (top-bar) — scopes the list to this supplier. */
  supplierPartyId?: number | null;
  buyerCode?: string;
  supplierCode?: string;
  poNumber?: string;
  dateFrom?: string;
  dateTo?: string;
  currentOnly?: boolean;
  pageIndex?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

// ── Canonical drill-down ──────────────────────────────────────────────────────

export interface PoExtra {
  code?: string | null;
  label?: string | null;
  content?: string | null;
}

export interface PoShipLoc {
  locType?: string | null;
  code?: string | null;
  label?: string | null;
  name?: string | null;
  city?: string | null;
  country?: string | null;
  locCode?: string | null;
  locCodeStd?: string | null;
  contPerson?: string | null;
  contPhone?: string | null;
}

export interface PoLine {
  id: number;
  sequence?: number | null;
  poLineNum?: string | null;
  customerOrderNum?: string | null;
  sku?: string | null;
  upc?: string | null;
  ean?: string | null;
  style?: string | null;
  color?: string | null;
  size?: string | null;
  fullDesc?: string | null;
  packType?: string | null;
  orderQty?: number | null;
  unitPrice?: number | null;
  prepackCode?: string | null;
  prepackStyle?: string | null;
  packFactor?: number | null;
  unitsPerInner?: number | null;
  unitsPerCarton?: number | null;
  shipDate?: string | null;
  shipCancelDate?: string | null;
  // Additional vendor/buyer line identifiers + labelling (spec mlinkone-po-additional-fields).
  // Carried but default-hidden in the PO-line view this slice.
  vendorStyle?: string | null;
  vendorColor?: string | null;
  vendorSize?: string | null;
  vendorItemNum?: string | null;
  buyerItemNum?: string | null;
  lineStatusCode?: string | null;
  labellingInstructions?: string | null;
  itemId?: number | null;
  /** For a PREPACK line — the assortment's own PACK master item id (resolved by PREPACK_CODE at the PO's
   *  pair). `itemId` is a borrowed first-component SKU, so this is what the UI opens for the prepack. */
  prepackItemId?: number | null;
  itemKey?: string | null;
  packGroupKey?: string | null;
  extras: PoExtra[];
  shipLocs: PoShipLoc[];
}

export interface PoVersionSummary {
  poId: number;
  versionNo: number;
  revisionNum?: string | null;
  contentHash?: string | null;
  isCurrent: boolean;
  createdAt?: string | null;
}

export interface PoCanonicalResponse {
  poId: number;
  poNumber: string;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  senderCode?: string | null;
  receiverCode?: string | null;
  versionNo: number;
  revisionNum?: string | null;
  /** DOC850 DOCPURPOSE raw code (00 Original, 01 Cancellation, 03 Delete, …). 01/03 block packing. */
  docPurpose?: string | null;
  contentHash?: string | null;
  sourceFormat?: string | null;
  dataSource?: string | null;
  brandCode?: string | null;
  brandName?: string | null;
  seasonCode?: string | null;
  orderQty?: number | null;
  orderDate?: string | null;
  msgSendDate?: string | null;
  sourceJson?: string | null;
  lines: PoLine[];
  headerExtras: PoExtra[];
  headerShipLocs: PoShipLoc[];
  versions: PoVersionSummary[];
  message: string;
}

// ── Ingest (synchronous batch) ────────────────────────────────────────────────

export interface PoIngestFileResult {
  fileName: string;
  status: 'ingested' | 'duplicate' | 'parse_failed' | 'unsupported_format' | 'error';
  poId?: number | null;
  versionNo?: number | null;
  lineCount?: number | null;
  contentHash?: string | null;
  message?: string | null;
}

export interface PoIngestBatchSummary {
  totalFiles: number;
  ingested: number;
  duplicates: number;
  rejectedNotEnrolled: number;
  parseFailed: number;
  unsupportedFormat: number;
  error: number;
}

export interface PoIngestBatchResponse {
  results: PoIngestFileResult[];
  summary: PoIngestBatchSummary;
  message: string;
}

// ── Rejected + reprocess ──────────────────────────────────────────────────────

export interface PoRejectedItem {
  sourceDocId: number;
  poNumber: string;
  senderCode?: string | null;
  receiverCode?: string | null;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  sourceFormat?: string | null;
  sourceFileName?: string | null;
  primaryReason?: string | null;
  createdAt?: string | null;
}

export interface PoRejectedListResponse {
  items: PoRejectedItem[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  message: string;
}

export interface PoReprocessResponse {
  sourceDocId: number;
  activated: boolean;
  stillRejected: boolean;
  newStatus: string;
  poId?: number | null;
  message: string;
}

export interface PoRejectedParams {
  tenantId: number;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  dateFrom?: string;
  dateTo?: string;
  pageIndex?: number;
  pageSize?: number;
}

/** Drill-down read timeout — sequential child reads; matches the ASN canonical 60s budget. */
const CANONICAL_TIMEOUT_MS = 60_000;
const LIST_TIMEOUT_MS = 60_000;
/** Synchronous batch ingest — N files processed server-side before the response; generous budget. */
const INGEST_TIMEOUT_MS = 600_000;

export const poWorkbenchService = {
  async listPo(params: PoListParams, signal?: AbortSignal): Promise<PoListResponse> {
    const res = await apiClient.get<ResultEnvelope<PoListResponse>>(API_MLINKONE_PO_LIST, {
      params: {
        tenantId: params.tenantId,
        buyerPartyId: params.buyerPartyId ?? undefined,
        supplierPartyId: params.supplierPartyId ?? undefined,
        buyerCode: params.buyerCode || undefined,
        supplierCode: params.supplierCode || undefined,
        poNumber: params.poNumber || undefined,
        dateFrom: params.dateFrom || undefined,
        dateTo: params.dateTo || undefined,
        currentOnly: params.currentOnly ?? true,
        pageIndex: params.pageIndex ?? 1,
        pageSize: params.pageSize ?? 50,
        sortBy: params.sortBy || undefined,
        sortDir: params.sortDir || undefined,
      },
      timeout: LIST_TIMEOUT_MS,
      signal,
    });
    if (!res.data?.succeeded || !res.data.data) {
      throw new Error(res.data?.messages?.[0] ?? 'Failed to load PO list.');
    }
    return res.data.data;
  },

  async ingestPoBatch(tenantId: number, files: File[], signal?: AbortSignal): Promise<PoIngestBatchResponse> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    const res = await apiClient.post<ResultEnvelope<PoIngestBatchResponse>>(API_MLINKONE_PO_INGEST, form, {
      params: { tenantId },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: INGEST_TIMEOUT_MS,
      signal,
    });
    if (!res.data?.succeeded || !res.data.data) {
      throw new Error(res.data?.messages?.[0] ?? 'PO import failed.');
    }
    return res.data.data;
  },

  async listRejectedPo(params: PoRejectedParams, signal?: AbortSignal): Promise<PoRejectedListResponse> {
    const res = await apiClient.get<ResultEnvelope<PoRejectedListResponse>>(API_MLINKONE_PO_REJECTED, {
      params: {
        tenantId: params.tenantId,
        buyerPartyId: params.buyerPartyId ?? undefined,
        supplierPartyId: params.supplierPartyId ?? undefined,
        dateFrom: params.dateFrom || undefined,
        dateTo: params.dateTo || undefined,
        pageIndex: params.pageIndex ?? 1,
        pageSize: params.pageSize ?? 50,
      },
      timeout: LIST_TIMEOUT_MS,
      signal,
    });
    if (!res.data?.succeeded || !res.data.data) {
      throw new Error(res.data?.messages?.[0] ?? 'Failed to load rejected POs.');
    }
    return res.data.data;
  },

  async reprocessPo(tenantId: number, sourceDocId: number): Promise<PoReprocessResponse> {
    const res = await apiClient.post<ResultEnvelope<PoReprocessResponse>>(
      API_MLINKONE_PO_REPROCESS(sourceDocId), null, {
        params: { tenantId },
        timeout: INGEST_TIMEOUT_MS, // reprocess re-runs the full pipeline
      });
    if (!res.data?.succeeded || !res.data.data) {
      throw new Error(res.data?.messages?.[0] ?? 'Reprocess failed.');
    }
    return res.data.data;
  },

  async getPoCanonical(tenantId: number, poId: number, signal?: AbortSignal): Promise<PoCanonicalResponse> {
    const res = await apiClient.get<ResultEnvelope<PoCanonicalResponse>>(API_MLINKONE_PO_CANONICAL(poId), {
      params: { tenantId },
      timeout: CANONICAL_TIMEOUT_MS,
      signal,
    });
    if (!res.data?.succeeded || !res.data.data) {
      throw new Error(res.data?.messages?.[0] ?? 'Failed to load PO.');
    }
    return res.data.data;
  },
};
